import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  CreditCard,
  DollarSign,
 
  CheckCircle,
  Clock,
  AlertCircle,
  
  Eye,
  Download,
  
  Search,
  Target,
} from 'lucide-react';

import {
  fetchPaymentRequests,
  type PaymentRequest,
} from '../../services/PaymentService';

interface PaymentStats {
  totalPaid: number;
  pendingPayments: number;
  completedPayments: number;
  failedPayments: number;
}

const PaymentCenter: React.FC = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [paymentRequests, setPaymentRequests] = useState<PaymentRequest[]>([]);
  const [stats, setStats] = useState<PaymentStats>({
    totalPaid: 0,
    pendingPayments: 0,
    completedPayments: 0,
    failedPayments: 0,
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [error, setError] = useState('');

  useEffect(() => {
    loadPaymentRequests();
  }, [statusFilter]);

  const loadPaymentRequests = async () => {
    try {
      setLoading(true);
      const status = statusFilter !== 'all' ? statusFilter : undefined;
      const requests = await fetchPaymentRequests(status);
      setPaymentRequests(requests);

      // Calculate stats
      const totalPaid = requests
        .filter((req) => req.status === 'paid')
        .reduce((sum, req) => sum + req.amount, 0);

      setStats({
        totalPaid,
        pendingPayments: requests.filter((req) => req.status === 'pending')
          .length,
        completedPayments: requests.filter((req) => req.status === 'paid')
          .length,
        failedPayments: requests.filter((req) => req.status === 'overdue')
          .length,
      });
    } catch (error) {
      setError('Failed to load payment requests');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    if (theme === 'dark') {
      switch (status) {
        case 'pending':
          return 'text-yellow-300 bg-yellow-900/30';
        case 'paid':
          return 'text-green-300 bg-green-900/30';
        case 'overdue':
          return 'text-red-300 bg-red-900/30';
        case 'cancelled':
          return 'text-gray-300 bg-gray-800';
        default:
          return 'text-gray-300 bg-gray-800';
      }
    } else {
      switch (status) {
        case 'pending':
          return 'text-yellow-600 bg-yellow-100';
        case 'paid':
          return 'text-green-600 bg-green-100';
        case 'overdue':
          return 'text-red-600 bg-red-100';
        case 'cancelled':
          return 'text-gray-600 bg-gray-100';
        default:
          return 'text-gray-600 bg-gray-100';
      }
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'paid':
        return <CheckCircle className="w-4 h-4" />;
      case 'overdue':
        return <AlertCircle className="w-4 h-4" />;
      case 'cancelled':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  const getRelativeTime = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now.getTime() - time.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  const filteredPaymentRequests = paymentRequests.filter((request) => {
    const matchesSearch =
      request.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request?.projectTitle?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === 'all' || request.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="space-y-6">
        <div
          className={`h-16 ${
            theme === 'dark' ? 'bg-gray-800' : 'bg-gray-200'
          } rounded-lg animate-pulse`}
        ></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, index) => (
            <div
              key={index}
              className={`${
                theme === 'dark'
                  ? 'bg-gray-800 border-gray-700'
                  : 'bg-white border-gray-200'
              } p-6 rounded-lg shadow-sm border animate-pulse`}
            >
              <div
                className={`h-4 ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                } rounded w-20 mb-4`}
              ></div>
              <div
                className={`h-8 ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                } rounded w-16 mb-2`}
              ></div>
              <div
                className={`h-3 ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                } rounded w-24`}
              ></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1
            className={`text-2xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}
          >
            Payment Center
          </h1>
          <p
            className={`${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            } mt-1`}
          >
            Manage payments and requests
          </p>
        </div>
      </div>

      {error && (
        <div
          className={`${
            theme === 'dark'
              ? 'bg-red-900/20 border-red-800'
              : 'bg-red-50 border-red-200'
          } border rounded-lg p-4`}
        >
          <div className="flex items-center">
            <AlertCircle
              className={`w-5 h-5 ${
                theme === 'dark' ? 'text-red-400' : 'text-red-500'
              } mr-2`}
            />
            <span
              className={`${
                theme === 'dark' ? 'text-red-300' : 'text-red-700'
              } text-sm`}
            >
              {error}
            </span>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div
          className={`${
            theme === 'dark'
              ? 'bg-gray-800 border-gray-700'
              : 'bg-white border-gray-200'
          } p-6 rounded-lg shadow-sm border`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p
                className={`text-sm font-medium ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}
              >
                Total Paid
              </p>
              <p
                className={`text-2xl font-bold ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}
              >
                {formatCurrency(stats.totalPaid)}
              </p>
            </div>
            <div
              className={`w-12 h-12 ${
                theme === 'dark' ? 'bg-green-900/30' : 'bg-green-100'
              } rounded-lg flex items-center justify-center`}
            >
              <DollarSign
                className={`w-6 h-6 ${
                  theme === 'dark' ? 'text-green-400' : 'text-green-600'
                }`}
              />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <CheckCircle
              className={`w-4 h-4 ${
                theme === 'dark' ? 'text-green-400' : 'text-green-500'
              } mr-1`}
            />
            <span
              className={`${
                theme === 'dark' ? 'text-green-400' : 'text-green-600'
              }`}
            >
              {stats.completedPayments} completed
            </span>
          </div>
        </div>

        <div
          className={`${
            theme === 'dark'
              ? 'bg-gray-800 border-gray-700'
              : 'bg-white border-gray-200'
          } p-6 rounded-lg shadow-sm border`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p
                className={`text-sm font-medium ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}
              >
                Pending Payments
              </p>
              <p
                className={`text-2xl font-bold ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}
              >
                {stats.pendingPayments}
              </p>
            </div>
            <div
              className={`w-12 h-12 ${
                theme === 'dark' ? 'bg-yellow-900/30' : 'bg-yellow-100'
              } rounded-lg flex items-center justify-center`}
            >
              <Clock
                className={`w-6 h-6 ${
                  theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'
                }`}
              />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <span
              className={`${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}
            >
              Awaiting processing
            </span>
          </div>
        </div>

        <div
          className={`${
            theme === 'dark'
              ? 'bg-gray-800 border-gray-700'
              : 'bg-white border-gray-200'
          } p-6 rounded-lg shadow-sm border`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p
                className={`text-sm font-medium ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}
              >
                Completed Payments
              </p>
              <p
                className={`text-2xl font-bold ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}
              >
                {stats.completedPayments}
              </p>
            </div>
            <div
              className={`w-12 h-12 ${
                theme === 'dark' ? 'bg-blue-900/30' : 'bg-blue-100'
              } rounded-lg flex items-center justify-center`}
            >
              <CheckCircle
                className={`w-6 h-6 ${
                  theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                }`}
              />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <span
              className={`${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}
            >
              Successfully processed
            </span>
          </div>
        </div>

        <div
          className={`${
            theme === 'dark'
              ? 'bg-gray-800 border-gray-700'
              : 'bg-white border-gray-200'
          } p-6 rounded-lg shadow-sm border`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p
                className={`text-sm font-medium ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}
              >
                Overdue Payments
              </p>
              <p
                className={`text-2xl font-bold ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}
              >
                {stats.failedPayments}
              </p>
            </div>
            <div
              className={`w-12 h-12 ${
                theme === 'dark' ? 'bg-red-900/30' : 'bg-red-100'
              } rounded-lg flex items-center justify-center`}
            >
              <AlertCircle
                className={`w-6 h-6 ${
                  theme === 'dark' ? 'text-red-400' : 'text-red-600'
                }`}
              />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <span
              className={`${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}
            >
              Require attention
            </span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div
        className={`${
          theme === 'dark'
            ? 'bg-gray-800 border-gray-700'
            : 'bg-white border-gray-200'
        } p-6 rounded-lg shadow-sm border`}
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search
                className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
                  theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                } w-4 h-4`}
              />
              <input
                type="text"
                placeholder="Search payments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`pl-10 pr-4 py-2 w-full border ${
                  theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
              />
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`px-3 py-2 border ${
                theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <button
              className={`flex items-center px-4 py-2 ${
                theme === 'dark'
                  ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              } rounded-lg transition-colors duration-200`}
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Payment Requests List */}
      <div
        className={`${
          theme === 'dark'
            ? 'bg-gray-800 border-gray-700'
            : 'bg-white border-gray-200'
        } rounded-lg shadow-sm border`}
      >
        <div
          className={`p-6 border-b ${
            theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
          }`}
        >
          <h2
            className={`text-lg font-semibold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}
          >
            Payment Requests
          </h2>
        </div>

        <div className="p-6">
          {filteredPaymentRequests.length === 0 ? (
            <div className="text-center py-12">
              <CreditCard
                className={`w-16 h-16 ${
                  theme === 'dark' ? 'text-gray-700' : 'text-gray-300'
                } mx-auto mb-4`}
              />
              <h3
                className={`text-lg font-medium ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                } mb-2`}
              >
                No payment requests found
              </h3>
              <p
                className={`${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                } mb-6`}
              >
                {paymentRequests.length === 0
                  ? "You don't have any payment requests at the moment."
                  : 'Try adjusting your search or filter criteria.'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredPaymentRequests.map((request) => (
                <div
                  key={request._id}
                  className={`border ${
                    theme === 'dark'
                      ? 'border-gray-700 hover:bg-gray-750'
                      : 'border-gray-200 hover:bg-gray-50'
                  } rounded-lg p-6 hover:shadow-md transition-colors duration-200`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <div
                          className={`flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                            request.status
                          )}`}
                        >
                          {getStatusIcon(request.status)}
                          <span className="ml-2 capitalize">
                            {request.status}
                          </span>
                        </div>
                        <span
                          className={`text-sm ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                          }`}
                        >
                          Project: {request.projectTitle}
                        </span>
                      </div>

                      <h3
                        className={`font-semibold ${
                          theme === 'dark' ? 'text-white' : 'text-gray-900'
                        } mb-2`}
                      >
                        {request.description}
                      </h3>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span
                            className={`${
                              theme === 'dark'
                                ? 'text-gray-400'
                                : 'text-gray-600'
                            }`}
                          >
                            Due Date:
                          </span>
                          <p
                            className={`font-medium ${
                              theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}
                          >
                            {new Date(request.dueDate).toLocaleDateString()}
                          </p>
                        </div>

                        <div>
                          <span
                            className={`${
                              theme === 'dark'
                                ? 'text-gray-400'
                                : 'text-gray-600'
                            }`}
                          >
                            Requested:
                          </span>
                          <p
                            className={`font-medium ${
                              theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}
                          >
                            {getRelativeTime(request.createdAt)}
                          </p>
                        </div>

                        {request.status === 'paid' && request.paidAt && (
                          <div>
                            <span
                              className={`${
                                theme === 'dark'
                                  ? 'text-gray-400'
                                  : 'text-gray-600'
                              }`}
                            >
                              Paid On:
                            </span>
                            <p
                              className={`font-medium ${
                                theme === 'dark'
                                  ? 'text-green-400'
                                  : 'text-green-600'
                              }`}
                            >
                              {new Date(request.paidAt).toLocaleDateString()}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-4 ml-6">
                      <div className="text-right">
                        <div
                          className={`text-2xl font-bold ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                          }`}
                        >
                          {formatCurrency(request.amount, request.currency)}
                        </div>
                      </div>
                      <Link
                        to={`/dashboard/payments/requests/${request._id}`}
                        className={`p-2 ${
                          theme === 'dark'
                            ? 'text-gray-400 hover:text-blue-400 hover:bg-gray-700'
                            : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                        } rounded-lg transition-colors duration-200`}
                        title="View details"
                      >
                        <Eye className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div
          className={`${
            theme === 'dark'
              ? 'bg-gradient-to-r from-blue-800 to-blue-900'
              : 'bg-gradient-to-r from-blue-500 to-blue-600'
          } p-6 rounded-lg text-white`}
        >
          <Target className="w-8 h-8 mb-4" />
          <h3 className="text-lg font-semibold mb-2">Payment Methods</h3>
          <p className="text-blue-100 mb-4">
            Manage your payment methods and billing information
          </p>
          <Link
            to="/dashboard/payments/methods"
            className={`inline-flex items-center px-4 py-2 ${
              theme === 'dark'
                ? 'bg-white/10 hover:bg-white/20'
                : 'bg-white text-blue-600'
            } rounded-lg hover:bg-gray-100 transition-colors duration-200`}
          >
            Manage Methods
          </Link>
        </div>

        <div
          className={`${
            theme === 'dark'
              ? 'bg-gradient-to-r from-green-800 to-green-900'
              : 'bg-gradient-to-r from-green-500 to-green-600'
          } p-6 rounded-lg text-white`}
        >
          <CreditCard className="w-8 h-8 mb-4" />
          <h3 className="text-lg font-semibold mb-2">Payment History</h3>
          <p className="text-green-100 mb-4">
            View your complete payment history and receipts
          </p>
          <Link
            to="/dashboard/payments/history"
            className={`inline-flex items-center px-4 py-2 ${
              theme === 'dark'
                ? 'bg-white/10 hover:bg-white/20'
                : 'bg-white text-green-600'
            } rounded-lg hover:bg-gray-100 transition-colors duration-200`}
          >
            View History
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PaymentCenter;
